/**
 * Hook for Founder Subscriber-View Kill Switch
 *
 * Reads `admin_config/featureFlags.subscriberVerificationConsoleEnabled` from
 * Firestore via `onSnapshot` so the founder can flip it without redeploy.
 *
 * Default: enabled = true if the doc/field is missing (fail-open). The page is
 * already gated by `AdminRouteGuard` (founder + MFA) so this kill switch is
 * an emergency-disable lever, not the primary access control.
 *
 * @see Plan: i-need-a-way-giggly-babbage.md (Wave 2 — Console UI)
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface UseFounderViewKillSwitchReturn {
  enabled: boolean;
  loading: boolean;
  error: string | null;
}

const FLAG_FIELD = 'subscriberVerificationConsoleEnabled';

export function useFounderViewKillSwitch(): UseFounderViewKillSwitchReturn {
  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ref = doc(db, 'admin_config', 'featureFlags');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          // Fail-open: doc missing → assume enabled.
          setEnabled(true);
        } else {
          const data = snap.data() as Record<string, unknown>;
          const value = data[FLAG_FIELD];
          // Only treat an explicit `false` as disabled. Missing/undefined → enabled.
          setEnabled(value === false ? false : true);
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        // Fail-open on read error so a transient Firestore blip doesn't lock
        // the founder out of an emergency-diagnostic surface. Surface the
        // error for visibility per the Codebase "no silent-fail" rule.
        console.error('[useFounderViewKillSwitch] onSnapshot error:', err);
        setError(err.message);
        setEnabled(true);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  return { enabled, loading, error };
}
